import { useEffect } from 'react';
import { confirmDialog, type ConfirmOptions } from '../components/ConfirmDialog';

/**
 * While `dirty`, asks (with `ask`, a constant) before the page is left: the tab
 * closed or reloaded (beforeunload), and any in-app link followed — a click on
 * an `<a href="#/…">` anywhere (the sidebar, a header link, a link of the page)
 * is caught before the router sees it, and followed only when confirmed.
 * The browser's own Back is not covered: the app's HashRouter cannot block it.
 */
export const useLeaveGuard = (dirty: boolean, ask: ConfirmOptions): void => {
  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
      const href = link?.getAttribute('href') || '';
      if (!href.startsWith('#/') || link?.getAttribute('target') === '_blank' || href === window.location.hash) return;
      e.preventDefault();
      e.stopPropagation();
      void confirmDialog(ask).then((leave) => {
        if (leave) window.location.hash = href;
      });
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, ask]);
};
